import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TechniciansFilter } from "@/components/common/section-components/filters/TechniciansFilter";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { isEmpty } from "@/utils/query-string";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { TechniciansActions } from "@/components/common/section-components/action/TechniciansActions";
import {
  TechnicianListEntity,
  TechniciansListResponse,
  techniciansFilterSchema,
} from "./schema/technicians.schema";
import { GetTechniciansListProps } from "@/services/technicians.service";
import { techniciansHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  fetchTechniciansHandler,
  handleDeleteTechniciansHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";

export const TechniciansPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, [isActive]);

  const navigate = useNavigate();
  const handleAddTechnician = () => {
    navigate(AppRoutes.TECHNICIAN_CREATION);
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.TECHNICIAN_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <TechniciansActions handleDetail={handleDetail} item={item} />
  );

  const { hasWriteAccess, fetchPermissions } = useRoles();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(techniciansFilterSchema),
  });

  const [loadingTechnicians, setLoadingTechnicians] = useState<boolean>(false);
  const [technicians, setTechnicians] = useState<TechniciansListResponse | null>(
    null,
  );
  const [loadingDeleteTechnicians, setLoadingDeleteTechnicians] =
    useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetTechniciansListProps> | undefined
  >();

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const { open: openConfirm, setOpen: setOpenConfirmModal } = useDisclosure();

  const fetchTechnicians = async (
    filters?: Partial<GetTechniciansListProps>,
    showLoad = true,
  ) => {
    await fetchTechniciansHandler({
      setLoadingTechnicians,
      setTechnicians,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      fetchTechnicians,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters: async () =>
        submitFiltersHandler({
          event: getValues(),
          setCurrentFilters,
          setFiltersApplied,
          setOpenFilters,
          setPage,
          fetchTechnicians,
        }),
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchTechnicians();
    }
  }, [isActive]);

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchTechnicians({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDelete = async () => {
    await handleDeleteTechniciansHandler({
      selection,
      setLoadingDeleteTechnicians,
      setOpenConfirm: setOpenConfirmModal,
      fetchTechnicians,
      setSelection,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="technicians-page">
      <SectionHeader
        title={"Maestro de técnicos"}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddTechnician}
        handleDelete={() => setOpenConfirmModal(true)}
        disableDelete={!selection?.length}
        loading={loadingTechnicians}
        showButtons={hasWriteAccess(PermissionCode.TECHNICIANS)}
      />

      <TechniciansFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingTechnicians}
        loadingFilters={loadingTechnicians}
        filtersApplied={filtersApplied}
        disableClear={loadingTechnicians}
        disableApply={loadingTechnicians || filtersEmpty}
      />

      <Flex flexDir="column">
        {Boolean(!loadingTechnicians && !technicians?.listado?.length) && <NoData />}
        {Boolean(!loadingTechnicians && technicians?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Edición"
              items={
                technicians?.listado?.map((el: TechnicianListEntity) => ({
                  ...el,
                  estado: el?.activo ? "Activo" : "Inactivo",
                })) ?? []
              }
              headers={techniciansHeaderElements}
              actionElements={getActionElements}
              noActions={!hasWriteAccess(PermissionCode.TECHNICIANS)}
              noSelection={!hasWriteAccess(PermissionCode.TECHNICIANS)}
            />

            {Boolean(technicians?.totalPages && technicians?.totalPages > 1) && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={technicians?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirmModal}
          title={`Eliminar técnicos`}
          subtitle={`¿Desea eliminar los técnicos (${selection?.length}) seleccionados? No podrá deshacer esta acción después.`}
          loadingAccept={loadingDeleteTechnicians}
          handleAccept={handleDelete}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
